import React, { useEffect, useState, useContext } from "react";
import { Popover, Badge, Empty, Spin, Modal } from "antd";
import { BellOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import useFetch from "../../hooks/useFetch";
import {
  GetNotifications,
  MarkNotificationRead,
} from "../../utils/api.constant";
import { eResultCode } from "../../utils/enum";
import TaskDetailsModal from "../task-card/task-details";
import styles from "./notifications.module.css";
import { UserContext } from "../../context";

dayjs.extend(relativeTime);

type TNotification = {
  id: number;
  taskId: number;
  notifyUserId: number;
  title: string;
  message: string;
  isRead: number; // 0 or 1
  createdby: number;
  createdon: string;
  updatedby: number;
  updatedon: string | null;
  isdeleted: number;
  deletedby: number;
  deletedon: string | null;
  task: {
    id: number;
    name: string;
    description: string;
    projectId: number;
    statusId: number;
    projectWorkMappingId: number;
    assignToUserId: number;
    startDateTime: string;
    endDateTime: string;
    priority: number;
    estimatedTime: number;
    actualTime: number | null;
    createdon: string;
    isRecurring: number;
    assignToUser?: {
      full_name: string;
    };
    assignedUser?: Array<{
      id: number;
      roleId: number;
      fullName: string;
      emailId: string;
      [key: string]: any;
    }>;
    taskStatus?: {
      name: string;
    };
    [key: string]: any;
  };
};

const Notifications: React.FC = () => {
  const { post } = useFetch();
  const context = useContext(UserContext);
  const [notifications, setNotifications] = useState<TNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<TNotification | null>(null);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await post(GetNotifications, {
        data: {},
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setNotifications(data || []);
        // Count unread notifications (isRead === 0)
        const unread = (data || []).filter(
          (n: TNotification) => n.isRead === 0
        ).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 90 seconds
    const interval = setInterval(fetchNotifications, 90000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId: number) => {
    try {
      await post(MarkNotificationRead, {
        data: {
          id: notificationId,
        },
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleNotificationClick = (notification: TNotification) => {
    setSelectedNotification(notification);
    setIsModalOpen(true);

    // Mark as read (optimistic update)
    if (notification.isRead === 0) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, isRead: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // Call API to mark notification as read
      markAsRead(notification.id);
    }

    setVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  };

  const NotificationList = () => {
    if (loading && notifications.length === 0) {
      return (
        <div className={styles.loadingContainer}>
          <Spin size='small' />
          <span>Loading notifications...</span>
        </div>
      );
    }

    if (notifications.length === 0) {
      return (
        <div className={styles.emptyContainer}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='No notifications'
          />
        </div>
      );
    }

    return (
      <div className={styles.notificationList}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`${styles.notificationItem} ${
              notification.isRead === 0 ? styles.unread : ""
            }`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className={styles.notificationContent}>
              <div className={styles.notificationTitle}>
                {notification.title}
              </div>
              <div className={styles.notificationMessage}>
                {notification.message}
              </div>
              <div className={styles.notificationTask}>
                Task: {notification.task?.name || "Unknown"}
              </div>
              <div className={styles.notificationMeta}>
                <span className={styles.notificationStatus}>
                  {notification.task?.taskStatus?.name || "N/A"}
                </span>
                <span className={styles.notificationTime}>
                  {dayjs(notification.createdon).fromNow()}
                </span>
              </div>
            </div>
            {notification.isRead === 0 && (
              <div className={styles.unreadIndicator} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Check if user is assigned to the selected task
  const isAssignedUser = selectedNotification
    ? selectedNotification.task?.assignToUserId === context?.user?.id ||
      selectedNotification.task?.assignedUser?.some(
        (user) => user.id === context?.user?.id
      )
    : false;

  return (
    <>
      <Popover
        content={<NotificationList />}
        title={
          <div className={styles.popoverHeader}>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <span className={styles.unreadText}>{unreadCount} unread</span>
            )}
          </div>
        }
        trigger='click'
        placement='rightBottom'
        open={visible}
        onOpenChange={setVisible}
        overlayClassName={styles.notificationPopover}
      >
        <div className={styles.notificationIcon}>
          <Badge
            count={unreadCount}
            overflowCount={99}
          >
            <BellOutlined style={{ fontSize: "22px", color: "white" }} />
          </Badge>
        </div>
      </Popover>

      {/* Task Details Modal */}
      <Modal
        title='Task Details'
        open={isModalOpen}
        onOk={() => {}}
        onCancel={handleModalCancel}
        footer={null}
        width='70%'
      >
        {selectedNotification ? (
          <TaskDetailsModal
            id={selectedNotification.task.id}
            isNotificationView={!isAssignedUser}
          />
        ) : (
          <p>No task selected.</p>
        )}
      </Modal>
    </>
  );
};

export default Notifications;
